// web/src/shell/MobileInterstitial.tsx
//
// Full-screen takeover shown on /dashboard when the viewport is too
// narrow for the two-pane trading dashboard. Owned by <MobileGate />.
//
// Because the overlay covers the entire viewport and is the only thing
// the user can interact with while it is up, it is modeled as a true
// modal: aria-modal="true", aria-describedby pointing at the body
// paragraph, and initial focus moved to the primary CTA so keyboard
// users have a sensible starting point.
import { useEffect, useId, useRef } from "react";
import { Monitor, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface Props {
  onContinue: () => void;
}

export function MobileInterstitial({ onContinue }: Props) {
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation("common");
  // useId() generates a stable, unique id per component instance so
  // aria-describedby points at the right paragraph even if (hypothetically)
  // two MobileInterstitials ever mount on the same page.
  const descriptionId = useId();

  // Move focus to the primary CTA when the interstitial mounts so a
  // keyboard user who lands here from the address bar or browser chrome
  // has somewhere to act. The autofocus happens after layout, which
  // matches how screen readers announce dialog content.
  useEffect(() => {
    continueButtonRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("mobileInterstitial.dialogLabel")}
      aria-describedby={descriptionId}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background p-6 text-center"
    >
      <Monitor className="mb-4 h-12 w-12 text-primary" aria-hidden="true" />
      <h1 className="mb-2 text-2xl font-semibold tracking-tight">
        {t("mobileInterstitial.title")}
      </h1>
      <p id={descriptionId} className="mb-6 max-w-md text-sm text-muted-foreground">
        {t("mobileInterstitial.body")}
      </p>
      <Button ref={continueButtonRef} onClick={onContinue} variant="outline">
        {t("mobileInterstitial.continue")}{" "}
        {/* C5: the arrow points toward the "continue" action — flip in RTL
            so it still points to the inline-end edge (toward the page
            content), matching the trailing margin direction. */}
        <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
      </Button>
    </div>
  );
}
